import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useAppConfig, useUser } from '@charlava-com/auth';

/**
 * Bare redirect route. Hitting `/signup` immediately kicks off the
 * Cognito hosted-UI sign-up flow (Cognito's `/signup` endpoint, not
 * `/login`). Used by external entry points (jamcam-web navbar CTA) so
 * we never have to hardcode the Cognito domain outside the auth lib.
 *
 * NOTE: We must wait until UserProvider has loaded AppConfig and
 * called `configureAws` before invoking `signUp()`; otherwise Amplify
 * has no OAuth config yet and `Auth.federatedSignIn()` silently no-ops.
 * The user state also has to settle (isLoading=false) so we know
 * Amplify has been configured by the parent's effect.
 */
export default function SignUpRedirect() {
  const [userState, userActions] = useUser();
  const { data: appConfig, isLoading: configLoading } = useAppConfig();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) return;
    if (configLoading || !appConfig) return;
    if (userState.isLoading) return;
    // If already signed in, no need to redirect to signup.
    if (userState.data) return;
    triggeredRef.current = true;
    userActions.signUp();
  }, [
    appConfig,
    configLoading,
    userActions,
    userState.data,
    userState.isLoading,
  ]);

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Redirecting you to sign up&hellip;
        </Typography>
      </Stack>
    </Box>
  );
}
